import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { v4 as uuidv4 } from 'uuid';
import { computed, inject } from '@angular/core';
import { FuelSpecsStore } from '@navstation-portal/fuel-specs/data-access/store';
import { ReportStore } from '@navstation-portal/reporting/data-access/vessel-report';
import {
  AnyOffshoreSectionState,
  OffshoreConsumptionsState,
  OffshoreFormConfig,
  OffshoreFuelSpecFormState,
  OffshoreFuelSpecState,
} from '@navstation-portal/reporting/data-model/offshore-report';
import {
  BreakdownConsumptionsFormState,
  INIT_MODES,
  InertGasSystemState,
  InertGasSystemsFormState,
  InitMode,
  TotalConsumptionsFormState,
  TotalConsumptionsState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import { initStateField } from '../../../offshore-report-utils/store.utility';
import { createDynamicSectionState } from '../../offshore-vm-builder-helpers';
import { withOffshoreFormStoreReducer } from '../../with-offshore-form-store-reducer';
import { buildFuelSpecsTableVm, buildOffshoreConsumptionsVm } from './offshore-consumptions-vm-builders';
import { withOffshoreConsumptionsEffects } from './with-offshore-consumptions-effects';
import { offshoreConsumptionsFormEvents } from './with-offshore-consumptions-events';
import { withOffshoreConsumptionsReducer } from './with-offshore-consumptions-reducer';

const generateFuelSpec = (fuelSpec?: OffshoreFuelSpecState): OffshoreFuelSpecFormState => {
  return {
    rowId: initStateField<string>(uuidv4()),
    fuelType: initStateField<SelectOption>(
      fuelSpec?.fuelType
        ? {
            name: (fuelSpec.fuelType as unknown as string).toUpperCase(),
            code: fuelSpec.fuelType as unknown as string,
          }
        : undefined
    ),
    isoFuelGrade: initStateField<SelectOption>(
      fuelSpec?.isoFuelGrade
        ? {
            name: fuelSpec.isoFuelGrade as unknown as string,
            code: fuelSpec.isoFuelGrade as unknown as string,
          }
        : undefined
    ),
    lcv: initStateField<number>(fuelSpec?.lcv),
    sulphur: initStateField<number>(fuelSpec?.sulphur),
    density: initStateField<number>(fuelSpec?.density),
    fuelTemp: initStateField<number>(fuelSpec?.fuelTemp),
    viscosity: initStateField<number>(fuelSpec?.viscosity),
  };
};

const generateFuelSpecArray = (data?: OffshoreConsumptionsState): OffshoreFuelSpecFormState[] => {
  if (!data) return [generateFuelSpec()];

  return data.fuelSpecs?.map((fuelSpec) => generateFuelSpec(fuelSpec)) || [];
};

const generateTotalConsumption = (data?: TotalConsumptionsState): TotalConsumptionsFormState => {
  if (!data) {
    return {
      mainEngine: initStateField<number>(),
      auxEngines: initStateField<number>(),
      boiler: initStateField<number>(),
      igGenerator: initStateField<number>(),
      incinerator: initStateField<number>(),
      dpp: initStateField<number>(),
      otherEngines: initStateField<number>(),
      cargoRelated: initStateField<number>(),
      total: initStateField<number>(),
    };
  }

  return {
    mainEngine: initStateField<number>(data.mainEngine),
    auxEngines: initStateField<number>(data.auxEngines),
    boiler: initStateField<number>(data.boiler),
    igGenerator: initStateField<number>(data.igGenerator),
    incinerator: initStateField<number>(data.incinerator),
    dpp: initStateField<number>(data.dpp),
    otherEngines: initStateField<number>(data.otherEngines),
    cargoRelated: initStateField<number>(data.cargoRelated),
    total: initStateField<number>(data.total),
  };
};

const generateTotalConsumptionsArray = (data?: OffshoreConsumptionsState): TotalConsumptionsFormState[] => {
  if (!data?.totalConsumptions?.length) return [generateTotalConsumption()];
  return data?.totalConsumptions
    ?.map((c) => generateTotalConsumption(c))
    .filter((_c, i) => {
      const len = data?.fuelSpecs?.length || 0;
      return i < len;
    });
};

const generateInertGasSystemsObject = (data?: InertGasSystemState) => {
  const inertGasSystem: InertGasSystemsFormState = {
    from: initStateField<Date>(data?.from ?? undefined),
    to: initStateField<Date>(data?.to ?? undefined),
  };
  return inertGasSystem;
};

const generateInertGasSystemsArray = (data?: InertGasSystemState[]) => {
  if (!data) return [generateInertGasSystemsObject(), generateInertGasSystemsObject(), generateInertGasSystemsObject()];

  const result: InertGasSystemsFormState[] = data.map((item) => {
    return generateInertGasSystemsObject(item);
  });

  return result;
};

const generateAuxEnginesConsumption = (
  data?: OffshoreConsumptionsState,
  fuelSpec?: string | undefined
): BreakdownConsumptionsFormState => {
  if (!data || !fuelSpec?.length) {
    return {
      basicLoad: initStateField<number>(),
      ballastOperations: initStateField<number>(),
      cargoHeating: initStateField<number>(),
      tankCleaning: initStateField<number>(),
      cargoDischarging: initStateField<number>(),
      inertGasSystems: initStateField<number>(),
      bunkerHeating: initStateField<number>(),
    };
  }

  return {
    basicLoad: initStateField<number>(data.aeBasicLoad ? data.aeBasicLoad[fuelSpec] : undefined),
    ballastOperations: initStateField<number>(
      data.aeBallastOperations ? data.aeBallastOperations[fuelSpec] : undefined
    ),
    cargoHeating: initStateField<number>(data.aeCargoHeating ? data.aeCargoHeating[fuelSpec] : undefined),
    tankCleaning: initStateField<number>(data.aeTankCleaning ? data.aeTankCleaning[fuelSpec] : undefined),
    cargoDischarging: initStateField<number>(data.aeCargoShifting ? data.aeCargoShifting[fuelSpec] : undefined),
    inertGasSystems: initStateField<number>(data.aeInertGasSystems ? data.aeInertGasSystems[fuelSpec] : undefined),
    bunkerHeating: initStateField<number>(data.aeBunkerHeating ? data.aeBunkerHeating[fuelSpec] : undefined),
  };
};

const generateAuxEnginesConsumptionsArray = (data?: OffshoreConsumptionsState): BreakdownConsumptionsFormState[] => {
  const fuelSpecs = data?.fuelSpecs?.map((fuelSpec) => fuelSpec.fuelType?.code as string);

  return [
    generateAuxEnginesConsumption(data, fuelSpecs?.[0]),
    generateAuxEnginesConsumption(data, fuelSpecs?.[1]),
    generateAuxEnginesConsumption(data, fuelSpecs?.[2]),
  ];
};

const generateBoilerConsumption = (
  data?: OffshoreConsumptionsState,
  fuelSpec?: string | undefined
): BreakdownConsumptionsFormState => {
  if (!data || !fuelSpec?.length) {
    return {
      basicLoad: initStateField<number>(),
      ballastOperations: initStateField<number>(),
      cargoHeating: initStateField<number>(),
      tankCleaning: initStateField<number>(),
      cargoDischarging: initStateField<number>(),
      inertGasSystems: initStateField<number>(),
      bunkerHeating: initStateField<number>(),
    };
  }

  return {
    basicLoad: initStateField<number>(data.abBasicLoad ? data.abBasicLoad[fuelSpec] : undefined),
    ballastOperations: initStateField<number>(
      data.abBallastOperations ? data.abBallastOperations[fuelSpec] : undefined
    ),
    cargoHeating: initStateField<number>(data.abCargoHeating ? data.abCargoHeating[fuelSpec] : undefined),
    tankCleaning: initStateField<number>(data.abTankCleaning ? data.abTankCleaning[fuelSpec] : undefined),
    cargoDischarging: initStateField<number>(data.abCargoShifting ? data.abCargoShifting[fuelSpec] : undefined),
    inertGasSystems: initStateField<number>(data.abInertGasSystems ? data.abInertGasSystems[fuelSpec] : undefined),
    bunkerHeating: initStateField<number>(data.abBunkerHeating ? data.abBunkerHeating[fuelSpec] : undefined),
  };
};

const generateBoilerConsumptionsArray = (data?: OffshoreConsumptionsState): BreakdownConsumptionsFormState[] => {
  const fuelSpecs = data?.fuelSpecs?.map((fuelSpec) => fuelSpec.fuelType?.code as string);

  return [
    generateBoilerConsumption(data, fuelSpecs?.[0]),
    generateBoilerConsumption(data, fuelSpecs?.[1]),
    generateBoilerConsumption(data, fuelSpecs?.[2]),
  ];
};

function buildConsumptionsState(data?: OffshoreConsumptionsState, formConfig?: OffshoreFormConfig, isImport = true) {
  return {
    water: {
      fwgNonFunctional: initStateField<boolean>(),
      freshWaterProduced: initStateField<number>(),
      distilledWaterProduced: initStateField<number>(),
    },
    inertGasSystems: {
      entries: generateInertGasSystemsArray(),
    },
    cylinderOilConsumption: {
      oilCode: initStateField<SelectOption>(),
      oilBn: initStateField<number>(),
      oilDensityAt15: initStateField<number>(),
      oilTankTemperature: initStateField<number>(),
      oilConsumption: initStateField<number>(),
      oilKgConsumption: initStateField<number>(),
      oilSloc: initStateField<number>(),
    },
    fuelSpecs: { entries: generateFuelSpecArray(data) },
    totalConsumptions: { entries: generateTotalConsumptionsArray(data) },
    auxEngineConsumptions: { entries: generateAuxEnginesConsumptionsArray() },
    boilerConsumptions: { entries: generateBoilerConsumptionsArray() },
    dynamicSection: createDynamicSectionState([]),
  };
}

const initBlank = () => buildConsumptionsState();
const initWithCarryOver = (data: OffshoreConsumptionsState, formConfig?: OffshoreFormConfig) =>
  buildConsumptionsState(data, formConfig);
const initWithExistingReport = (data: OffshoreConsumptionsState, formConfig?: OffshoreFormConfig) => {
  return buildConsumptionsState(data, formConfig);
};

const initOffshoreConsumptionStateReducer = (initialData: AnyOffshoreSectionState, initMode: InitMode) => {
  const data = initialData as OffshoreConsumptionsState;

  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};

export const OffshoreConsumptionsFormStore = signalStore(
  { providedIn: 'root' },
  withDevtools('OffshoreConsumptionsFormStore'),
  withState(initBlank()),
  withOffshoreConsumptionsEffects(),
  withOffshoreFormStoreReducer(offshoreConsumptionsFormEvents, initOffshoreConsumptionStateReducer, initBlank),
  withOffshoreConsumptionsReducer(),
  withComputed((store) => {
    const vesselSpecStore = inject(VesselSpecStore);
    const fuelSpecsStore = inject(FuelSpecsStore);
    const reportStore = inject(ReportStore);
    const carryOverReport = reportStore.carryOverReport();

    return {
      fuelSpecsTableVm: computed(() => {
        const entries = store.fuelSpecs?.entries();
        const fuelSpecsVm = fuelSpecsStore.fuelspecsVm();
        return buildFuelSpecsTableVm(entries, fuelSpecsVm.getFuelSpecMinMaxValues);
      }),
      vm: computed(() => {
        const vesselSpec = vesselSpecStore.vesselSpec();
        const availableFuelTypeOptions = reportStore.availableFuelGradeOptions();
        const fuelSpecsVm = fuelSpecsStore.fuelspecsVm();
        return buildOffshoreConsumptionsVm(
          store,
          carryOverReport.power,
          vesselSpec,
          availableFuelTypeOptions,
          fuelSpecsVm.getFuelSpecMinMaxValues
        );
      }),
    };
  })
);
